import { Button, IconButton, Menu, MenuItem, Snackbar } from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import { MoreVert } from '@material-ui/icons'
import { AxiosInstance, AxiosResponse } from 'axios'
import React, { ComponentProps, FunctionComponent, ReactNode, useContext, useEffect, useState } from 'react'
import Moment from 'react-moment'
import useRowStyles from 'src/assets/css/components/RowComponentsStyles'
import alerts from 'src/assets/images/sharewith/alerts.svg'
import allactivity from 'src/assets/images/sharewith/all_activity.svg'
import arrowRight from 'src/assets/images/svgs/arrow_left.svg'
import reloadIcon from 'src/assets/images/svgs/reload.svg'
import startIcon from 'src/assets/images/svgs/play.svg'
import stopIcon from 'src/assets/images/svgs/square-icon.svg'
import Chart from 'src/components/charts/Chart'
import ChoiceModal from 'src/components/ChoiceModal'
import ConfirmDialog from 'src/components/ConfirmDialog'
import JobRow from 'src/components/JobRow'
import Spinner from 'src/components/Spinner'
import { APIContext } from 'src/contexts/api'
import { IDataRowProps } from 'src/models/components/IDataRow'
import { IJobRowProps } from 'src/models/components/IJobRow'
import IJob, { JobStatus } from 'src/models/IJob'
import IPage from 'src/models/IPage'
import IPipeline, { latestRun } from 'src/models/IPipeline'
import { SubscriptionType } from 'src/models/ISubscription'
import { logError } from 'src/utils/Error'
import { errorMessage } from 'src/utils/ingestionSplit'

interface IBackfillSectionProps extends Pick<IDataRowProps, 'openLog' | 'renderLogs' | 'downloadLog'>,
    Required<Pick<IJobRowProps, 'onDiagnose' | 'onCopyPrompt' | 'onPrepare'>> {
    // as the pipelines list carries it
    backfill: IPipeline
    metricsDataset: ComponentProps<typeof Chart>['dataset']
    jobMarker: (status: JobStatus) => string
    runLabels: (job: IJob) => ReactNode
    // absent where the backfill's pipeline cannot be backfilled
    onRestart?: (backfill: IPipeline) => Promise<void>
    onDelete: (backfill: IPipeline) => void
    diagnoseButton: (onClick: () => void) => ReactNode
    // whether its row shows it: a section that is not shown keeps what it has read, but follows no run
    isShown: boolean
}

const BackfillSection: FunctionComponent<IBackfillSectionProps> = ({ backfill, metricsDataset, jobMarker, runLabels, openLog, renderLogs, downloadLog, onRestart, onDelete, diagnoseButton, onDiagnose, onCopyPrompt, onPrepare, isShown }) => {
    const classes = useRowStyles()
    const api = useContext(APIContext).catalogAPI as AxiosInstance

    // the pipelines list omits the properties, which hold the backfill's dates
    const [detail, setDetail] = useState<IPipeline>()
    // a run's first attempt and its three default retries, on one page
    const [jobsHref, setJobsHref] = useState(`${backfill._links.jobs.href}?size=4`)
    const [jobs, setJobs] = useState<IPage<IJob>>()
    const [isExpanded, setExpanded] = useState(false)
    const [isStarting, setStarting] = useState(false)
    const [isStopping, setStopping] = useState(false)
    const [isRefreshing, setRefreshing] = useState(false)
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement>()
    const [isWatchDialogOpen, setWatchDialogOpen] = useState(false)
    const [jobToDelete, setJobToDelete] = useState<IJob>()
    const [isRestartDialogOpen, setRestartDialogOpen] = useState(false)
    const [restartError, setRestartError] = useState<string>()

    const readDetail = () => api.get(backfill._links.self.href).then((r: AxiosResponse<IPipeline>) => setDetail(r.data))
    const readJobs = () => api.get(jobsHref).then((r: AxiosResponse<IPage<IJob>>) => setJobs(r.data))
    // the runs show only in an expanded section
    const refresh = () => Promise.all([readDetail(), ...isExpanded ? [readJobs()] : []]).catch(logError)

    useEffect(() => {
        refresh()
    }, [backfill])

    useEffect(() => {
        isExpanded && readJobs().catch(logError)
    }, [jobsHref, isExpanded])

    const isActive = !!detail?._links['withdraw job']
    const lastRun = detail && latestRun(detail)
    const hasErrored = !!lastRun && [JobStatus.Error, JobStatus.Warning].includes(lastRun.status)

    // as the row follows its own run
    useEffect(() => {
        if (!isActive) {
            setStopping(false)
            return
        }
        if (!isShown)
            return

        const poll = setInterval(refresh, 5000)
        return () => clearInterval(poll)
    }, [detail, jobsHref, isExpanded, isShown])

    const handleRefresh = () => {
        setRefreshing(true)
        refresh().finally(() => setRefreshing(false))
    }

    const start = () => {
        if (isStarting)
            return

        setStarting(true)
        api.post(detail!._links['create job'].href).then(refresh).catch(logError).finally(() => setStarting(false))
    }
    // a stop is only requested, so the icon spins until a refresh finds the run ended
    const stop = () => {
        if (isStopping)
            return

        setStopping(true)
        api.put(detail!._links['withdraw job'].href).then(refresh).catch(e => {
            setStopping(false)
            logError(e)
        })
    }

    const watch = () => {
        setMenuAnchor(undefined)

        if (detail!._links['delete subscription']) {
            api.delete(detail!._links['delete subscription'].href).then(refresh).catch(logError)
            return
        }

        setWatchDialogOpen(true)
    }

    const subscribe = (type: SubscriptionType) => {
        setWatchDialogOpen(false)
        api.post(detail!._links['add subscription'].href, { type }).then(refresh).catch(logError)
    }

    const deleteJob = () => api.delete(jobToDelete!._links['delete job'].href).then(refresh).catch(logError)
    const restart = () => onRestart!(detail!).catch(e => {
        setRestartError(errorMessage(e))
        logError(e)
    })

    const extractor = detail?._embedded?.dataComponents.find(dc => dc.dataPluginType === 'extractor')
    const setting = (name: string) => extractor
        && (detail!.properties[`${extractor.name}.${name}`] || extractor.properties[name])
    // a rule names a stream, then its property or `*`; a `!` rule only excludes
    const select = extractor && detail!.properties[`${extractor.name}._select`]
    const rules: string[] = select ? JSON.parse(select) : []
    const streams = Array.from(new Set(rules.filter(rule => !rule.startsWith('!')).map(rule => rule.split('.')[0])))

    return (
        // hidden until it has its dates and streams, so its border shows no empty section
        <div className='backfill-section' hidden={!detail}>
            <ChoiceModal
                message="Watch for..."
                isOpen={isWatchDialogOpen}
                onClose={() => setWatchDialogOpen(false)}>
                <div className='item'>
                    <Button variant='text' onClick={() => subscribe(SubscriptionType.Alerts)} className="modal-button">
                        <img src={alerts} alt='icon' />
                        <div>Errors</div>
                    </Button>
                </div>
                <div className='item'>
                    <Button variant='text' onClick={() => subscribe(SubscriptionType.All)} className="modal-button">
                        <img src={allactivity} alt='icon' />
                        <div>All activity</div>
                    </Button>
                </div>
            </ChoiceModal>
            <ConfirmDialog
                isOpen={!!jobToDelete}
                title="Delete job?"
                message="Remove this job from the backfill. This action cannot be undone."
                cancelLabel="Close"
                confirmLabel="Delete"
                handleClose={() => setJobToDelete(undefined)}
                handleConfirm={deleteJob} />
            <ConfirmDialog
                isOpen={isRestartDialogOpen}
                title="Restart backfill?"
                message="Run this backfill again from its start date, with the same streams. Its run history is deleted."
                cancelLabel="Close"
                confirmLabel="Restart"
                handleClose={() => setRestartDialogOpen(false)}
                // not awaited: a restart replaces this section, so the dialog must close first
                handleConfirm={async () => { restart() }} />
            <Snackbar
                open={!!restartError}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                autoHideDuration={6000}
                onClose={() => setRestartError(undefined)}>
                <Alert severity='error'>Couldn’t restart the backfill: {restartError}</Alert>
            </Snackbar>
            {detail &&
                <div className="backfill-header">
                    <div className='row-labels'>
                        <span className='inline-vertical-center pipeline-date'>
                            <i className='fas fa-history' />
                            {setting('start_date')
                                ? <Moment format="YYYY-MM-DD" date={setting('start_date')} utc={true} />
                                : 'All time'}
                            {setting('end_date') && <>
                                &nbsp;to&nbsp;<Moment format="YYYY-MM-DD" date={setting('end_date')} utc={true} />
                            </>}
                        </span>
                        <span
                            className='inline-vertical-center pipeline-date'
                            title={[...rules.slice(0, 10), ...rules.length > 10 ? ['', `(and ${rules.length - 10} more)`] : []].join('\n')}>
                            <i className='fas fa-stream' />
                            {!streams.length
                                ? 'Default streams'
                                : `${streams.length} ${streams.length === 1 ? 'stream' : 'streams'}`}
                        </span>
                        {!isExpanded && latestRun(detail) && runLabels(latestRun(detail)!)}
                        {hasErrored && diagnoseButton(() => onDiagnose(lastRun))}
                    </div>
                    <div className='inline-vertical-center'>
                        <div className='button-wrapper' onClick={handleRefresh} title="Refresh">
                            <span className='refresh-action'>
                                <Spinner active={isRefreshing}>
                                    <img src={reloadIcon} />
                                </Spinner>
                            </span>
                        </div>
                        {isActive
                            ? <div className='button-wrapper' onClick={stop} title="Stop job">
                                <span className='run-action' aria-label="Stop backfill job">
                                    <Spinner active={isStopping}>
                                        <img src={stopIcon} />
                                    </Spinner>
                                </span>
                            </div>
                            : <div
                                className={['button-wrapper', ...!detail._links['create job'] ? ['disabled'] : []].join(' ')}
                                title="Start job">
                                <span className='run-action' aria-label="Start backfill job" onClick={start}>
                                    <Spinner active={isStarting}>
                                        <img src={startIcon} />
                                    </Spinner>
                                </span>
                            </div>}
                        <div
                            className={`button-wrapper ${isExpanded ? 'collapse-center' : 'expand-center'}`}
                            onClick={() => setExpanded(!isExpanded)}
                            title={`${isExpanded ? 'Collapse' : 'Expand'}`}>
                            <span className={`info-action ${isExpanded ? 'expanded' : ''}`}>
                                <img src={arrowRight} />
                            </span>
                        </div>
                        <IconButton
                            className={classes.button}
                            aria-label='backfill-actions-menu'
                            onClick={e => setMenuAnchor(e.currentTarget)}
                            title="More">
                            <MoreVert className={classes.largeIcon} />
                        </IconButton>
                        <Menu
                            className={classes.dropdownMenu}
                            disableScrollLock
                            open={!!menuAnchor}
                            anchorEl={menuAnchor}
                            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            onClose={() => setMenuAnchor(undefined)}>
                            <MenuItem className={classes.dropdownItem} dense onClick={watch}>
                                <i className={'fas ' + (detail._links['delete subscription'] ? 'fa-eye-slash' : 'fa-eye')} />
                                <span>{detail._links['delete subscription'] ? "Unwatch" : "Watch"}</span>
                            </MenuItem>
                            <MenuItem
                                className={classes.dropdownItem}
                                dense
                                disabled={!onRestart || isActive}
                                onClick={() => {
                                    setMenuAnchor(undefined)
                                    setRestartDialogOpen(true)
                                }}>
                                <i className='fas fa-redo' />
                                <span>Restart</span>
                            </MenuItem>
                            <MenuItem
                                className={classes.dropdownItem}
                                dense
                                disabled={!detail._links['delete pipeline']}
                                onClick={() => {
                                    setMenuAnchor(undefined)
                                    onDelete(backfill)
                                }}>
                                <i className='fas fa-trash' />
                                <span>Delete</span>
                            </MenuItem>
                        </Menu>
                    </div>
                </div>}
            {isExpanded && <>
                {backfill._links['metrics'] &&
                    <div data-testid="metrics-chart" className="metrics-chart">
                        <Chart dataset={metricsDataset} refreshKey={detail} />
                    </div>}
                {jobs?._embedded?.jobs && <>
                    {jobs._embedded.jobs.map(job =>
                        <JobRow
                            key={job.id}
                            job={job}
                            jobMarker={jobMarker}
                            renderLogs={renderLogs}
                            openLog={openLog}
                            handleOpenJobConfirmDialog={setJobToDelete}
                            downloadLog={downloadLog}
                            onDiagnose={onDiagnose}
                            onCopyPrompt={onCopyPrompt}
                            onPrepare={onPrepare} />)}
                    <div className="pagination-buttons">
                        <button disabled={!jobs._links.prev} onClick={() => setJobsHref(jobs._links.prev!.href)}>
                            <img
                                className="arrow-left"
                                src={arrowRight}
                                title="Newer jobs"
                                alt="Newer jobs" />
                            Newer
                        </button>
                        <button disabled={!jobs._links.next} onClick={() => setJobsHref(jobs._links.next!.href)}>
                            Older
                            <img
                                className="arrow-right"
                                src={arrowRight}
                                title="Older jobs"
                                alt="Older jobs" />
                        </button>
                    </div>
                </>}
            </>}
        </div>
    )
}

export default BackfillSection
